"use client";

import type { FooterData, FooterGroup, LanguageOption } from "../../../types";
import { RuntimeImage, useLocale } from "../../../runtime/RendererRuntime";
import { GuardedLink, useDevPlaceholder } from "../../../runtime/DevPlaceholder";
import { t } from "../../../i18n/strings";
import { resolveConfiguredHref } from "../../../utils/links";
import { SocialDiscRow } from "./SocialDiscRow";
import { FooterPartners } from "./FooterPartners";
import { FooterLanguageLinks } from "./FooterLanguageLinks";

/**
 * Desktop footer — four columns:
 *   [Logo + primary address] [Visitez aussi nos pages] [Contact note] [Informations pratiques + socials]
 *
 * All copy is sourced from `FooterData` (CMS), so reordering, renaming, or
 * localizing happens through `siteSettings.json` rather than code edits.
 * Group titles are matched by their display title, but the renderer falls
 * back to the array order if titles change — making the layout resilient
 * to editorial wording tweaks.
 */
export function SiteFooterDesktop({
	data,
	logoSrc,
	logoAlt,
	language,
	languageOptions,
	onLanguageChange,
	resolveHref,
}: {
	data: FooterData;
	logoSrc: string;
	logoAlt: string;
	language?: string;
	languageOptions?: LanguageOption[];
	onLanguageChange?: (code: string) => void;
	resolveHref?: (code: string) => string;
}) {
	const { previewMode } = useDevPlaceholder();
	const locale = useLocale();
	const resourcesGroup = pickGroup(data.groups, ["Visitez aussi nos pages", "Visitez aussi"], 0);
	const practicalGroup = pickGroup(data.groups, ["Informations pratiques"], 1);
	const primaryAddress = data.addresses[0];
	const secondaryAddresses = data.addresses.slice(1);
	const contactNote = (data.contactNote ?? "").trim();
	// Production hides links with no destination; preview keeps them (clicking opens the dev modal).
	const keep = (href: unknown) => previewMode || Boolean(resolveConfiguredHref(href as never));
	const primaryActions = (primaryAddress?.actions ?? []).filter((action) => keep(action.href));
	const legalLinks = data.legalLinks.filter((link) => keep(link.href));

	// A link group whose destinations were all filtered out renders nothing, so
	// the grid has to shrink with it — otherwise the missing column leaves a hole
	// (which is exactly what happened once the catalogue links were withdrawn).
	const hasLinks = (group: FooterGroup | null) =>
		(group?.links ?? []).some((link) => keep(link.href));
	const linkColumns = [
		{ group: resourcesGroup, fallbackTitle: "Visitez aussi nos pages" },
		{ group: practicalGroup, fallbackTitle: "Informations pratiques" },
	].filter((column) => hasLinks(column.group));
	const gridColumns = linkColumns.length === 2
		? "lg:grid-cols-[1.15fr_1fr_1fr_1.25fr]"
		: linkColumns.length === 1
			? "lg:grid-cols-[1.15fr_1fr_1.25fr]"
			: "lg:grid-cols-[1.3fr_1.25fr]";

	return (
		<div className="hidden flex-col gap-10 md:flex">
			<div className={`grid gap-10 ${gridColumns} lg:items-start`}>
				{/* Column 1 — Brand: logo + tagline. The contact note doubles as the brand
				    statement so the column carries the same visual weight as its neighbours. */}
				<div className="space-y-5 text-sm xl:text-base text-white/85">
					<RuntimeImage src={logoSrc} alt={logoAlt} className="w-full max-w-[210px] h-auto object-contain" />
					{contactNote ? (
						<p className="max-w-[300px] text-sm xl:text-base leading-relaxed text-white/70">{contactNote}</p>
					) : null}
				</div>

				{/* Link columns — only those that still have somewhere to point. */}
				{linkColumns.map((column) => (
					<FooterLinkColumn key={column.fallbackTitle} group={column.group} fallbackTitle={column.fallbackTitle} />
				))}

				{/* Column 4 — Contact (address + actions) and social row, stacked so the
				    column balances the link lists beside it. */}
				<div className="flex flex-col gap-7">
					{primaryAddress && (
						<div className="text-sm xl:text-base text-white/85">
							<h5 className="apa-eyebrow apa-eyebrow-light">Contact</h5>
							<p className="mt-4 font-semibold text-white">{primaryAddress.title}</p>
							<div className="mt-1.5 space-y-1">
								{primaryAddress.lines.map((line, idx) => (
									<p key={idx}>{line}</p>
								))}
							</div>
							{primaryActions.length ? (
								<div className="mt-3.5 flex flex-wrap gap-2 text-xs xl:text-sm">
									{primaryActions.map((action) => (
										<GuardedLink
											key={action.label}
											label={action.label}
											href={action.href}
											className="inline-flex items-center rounded-full border border-white/20 px-3 py-1 font-medium text-white hover:bg-white/10"
										>
											{action.label}
										</GuardedLink>
									))}
								</div>
							) : null}
							{secondaryAddresses.length > 0 && (
								<div className="mt-4 space-y-4">
									{secondaryAddresses.map((addr) => (
										<div key={addr.title}>
											<p className="font-semibold text-white">{addr.title}</p>
											<div className="mt-1 space-y-1">
												{addr.lines.map((line, idx) => (
													<p key={idx}>{line}</p>
												))}
											</div>
										</div>
									))}
								</div>
							)}
						</div>
					)}
					{(data.socials ?? []).length > 0 ? (
						<div>
							<h5 className="apa-eyebrow apa-eyebrow-light">Suivez-nous</h5>
							<SocialDiscRow items={data.socials} className="mt-4" />
						</div>
					) : null}
				</div>
			</div>

			<div className="border-t border-white/20 pt-6 text-sm text-white/80">
				{legalLinks.length > 0 && (
					<nav aria-label="Informations légales" className="mb-5">
						<ul className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs uppercase tracking-wide text-white/65 xl:text-sm">
							{legalLinks.map((link) => (
								<li key={link.label}>
									<GuardedLink label={link.label} href={link.href} className="transition hover:text-white">
										{link.label}
									</GuardedLink>
								</li>
							))}
						</ul>
					</nav>
				)}
				{/* Legal navigation comes first; ownership and production credits
				    close the page as its final signature. */}
				<div className="flex flex-col items-center gap-4 text-center">
					<p>© {new Date().getFullYear()} APA Parts. {t(locale, "allRightsReserved")}</p>
					<FooterPartners />
					{languageOptions && onLanguageChange ? (
						<FooterLanguageLinks
							language={language ?? ""}
							languageOptions={languageOptions}
							onLanguageChange={onLanguageChange}
							resolveHref={resolveHref}
						/>
					) : null}
				</div>
			</div>
		</div>
	);
}

/** Renders one footer link column: bold title + vertical list. Hidden when
 *  the group is empty so the grid collapses gracefully. */
function FooterLinkColumn({ group, fallbackTitle }: { group: FooterGroup | null; fallbackTitle: string }) {
	const { previewMode } = useDevPlaceholder();
	const links = (group?.links ?? []).filter((link) => previewMode || resolveConfiguredHref(link.href));
	if (!group || links.length === 0) return <div />;
	return (
		<div>
			<h5 className="apa-eyebrow apa-eyebrow-light">{group.title || fallbackTitle}</h5>
			<ul className="mt-4 space-y-2 text-sm xl:text-base text-white/70">
				{links.map((link) => (
					<li key={link.label}>
						<GuardedLink
							label={link.label}
							className="inline-block transition hover:translate-x-0.5 hover:text-white"
							href={link.href}
						>
							{link.label}
						</GuardedLink>
					</li>
				))}
			</ul>
		</div>
	);
}

/** Find a group by trying any of the candidate titles, falling back to the
 *  Nth group by position when nothing matches. Lets the CMS rename groups
 *  without breaking the layout. */
function pickGroup(groups: FooterGroup[], titles: string[], fallbackIndex: number): FooterGroup | null {
	for (const title of titles) {
		const match = groups.find((g) => g.title === title);
		if (match) return match;
	}
	return groups[fallbackIndex] ?? null;
}
